/**
 * BiomechanicsVideoModal - Sophisticated video analysis modal with biomechanical markers
 *
 * Features:
 * - Video playback with timestamp-synced overlays
 * - File name display for video identification
 * - All biomechanical markers displayed with context
 * - Situation awareness (pitch type, runners, game context)
 * - Professional HUD-style marker visualization
 * - Timeline with clickable markers
 *
 * @version 1.0.0
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Play,
  Pause,
  X,
  Volume2,
  VolumeX,
  Maximize2,
  SkipBack,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Users,
  User,
  Target,
  Zap,
  Activity,
  Clock,
  FileVideo,
  Eye,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';
import { getConfidenceLevel, getConfidenceColor, PITCH_TYPE_LABELS } from '../../lib/wireTypes';
import { getVideoUrl } from '../../lib/wireDataLoader';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function parseTimestampToSeconds(timestampStr) {
  if (!timestampStr || typeof timestampStr !== 'string') return null;
  const match = timestampStr.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function parseTimestampsFromText(text) {
  const timestamps = [];
  const regex = /(\d{1,2}):(\d{2})/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    timestamps.push(parseInt(match[1], 10) * 60 + parseInt(match[2], 10));
  }
  return [...new Set(timestamps)].sort((a, b) => a - b);
}

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function extractFilename(gcsUri) {
  if (!gcsUri) return 'Unknown';
  const parts = gcsUri.split('/');
  return parts[parts.length - 1] || 'Unknown';
}

function formatSituation(situation) {
  if (!situation) return 'Unknown';
  const mapping = {
    'bases_empty': 'Bases Empty',
    'runner_on_first': 'Runner on 1st',
    'runner_on_second': 'Runner on 2nd',
    'runner_on_third': 'Runner on 3rd',
    'runners_on_first_second': 'Runners on 1st & 2nd',
    'runners_on_first_third': 'Runners on 1st & 3rd',
    'runners_on_second_third': 'Runners on 2nd & 3rd',
    'bases_loaded': 'Bases Loaded',
  };
  return mapping[situation] || situation.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// =============================================================================
// CONFIDENCE LEVEL BADGE
// =============================================================================

function ConfidenceBadge({ confidence, size = 'md' }) {
  const level = getConfidenceLevel(confidence);
  const color = getConfidenceColor(level);
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };

  return (
    <span
      className={`font-mono font-bold rounded ${sizeClasses[size]}`}
      style={{ backgroundColor: `${color}30`, color }}
    >
      {level} {(confidence * 100).toFixed(0)}%
    </span>
  );
}

// =============================================================================
// SITUATION BADGE
// =============================================================================

function SituationBadge({ situation }) {
  const hasRunners = situation && !situation.includes('empty');

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${hasRunners ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-700/50 text-zinc-400'}`}>
      {hasRunners ? <Users className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
      <span className="text-xs font-medium">{formatSituation(situation)}</span>
    </div>
  );
}

// =============================================================================
// PITCH TYPE BADGE
// =============================================================================

function PitchTypeBadge({ pitchType, size = 'md' }) {
  const label = PITCH_TYPE_LABELS[pitchType] || pitchType;
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  return (
    <span className={`bg-orange-500/20 text-orange-400 font-bold font-mono rounded ${sizeClasses[size]}`}>
      {pitchType}
    </span>
  );
}

// =============================================================================
// BIOMECHANICAL MARKER CARD
// =============================================================================

function BiomarkerCard({ marker, isActive, onClick, currentTime }) {
  const level = getConfidenceLevel(marker.confidence);
  const color = getConfidenceColor(level);
  const isNearTimestamp = marker.timestamp && Math.abs(currentTime - marker.timestamp) < 1;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
        isActive
          ? 'border-orange-500 bg-orange-500/10 ring-1 ring-orange-500/50'
          : isNearTimestamp
          ? 'border-blue-500/50 bg-blue-500/10'
          : 'border-zinc-700/50 bg-zinc-800/50 hover:border-zinc-600'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isActive || isNearTimestamp ? 'animate-pulse' : ''}`}
            style={{ backgroundColor: color }}
          />
          <span className="font-semibold text-white text-sm">{marker.tell}</span>
        </div>
        <ConfidenceBadge confidence={marker.confidence} size="sm" />
      </div>

      {marker.timestamp && (
        <div className="flex items-center gap-1.5 mb-2">
          <Clock className="w-3 h-3 text-zinc-500" />
          <span className="text-xs font-mono text-zinc-400">@ {formatTime(marker.timestamp)}</span>
        </div>
      )}

      {marker.description && (
        <p className={`text-xs text-zinc-400 leading-relaxed ${isActive ? '' : 'line-clamp-2'}`}>
          {marker.description}
        </p>
      )}

      {marker.pitchTypesAffected && marker.pitchTypesAffected.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <span className="text-[10px] text-zinc-500">Affects:</span>
          {marker.pitchTypesAffected.map(pt => (
            <span key={pt} className="px-1.5 py-0.5 bg-zinc-700 text-zinc-300 rounded text-[10px] font-mono">
              {pt}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// =============================================================================
// ACTIVE TELL OVERLAY (Shows on video during playback)
// =============================================================================

function ActiveTellOverlay({ marker, onDismiss }) {
  const level = getConfidenceLevel(marker.confidence);
  const color = getConfidenceColor(level);

  return (
    <div className="absolute top-3 left-3 right-3 z-20 animate-in fade-in slide-in-from-top-2 duration-300">
      <div
        className="rounded-xl border overflow-hidden shadow-2xl"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(12px)',
          borderColor: `${color}60`,
        }}
      >
        <div
          className="px-4 py-3 flex items-center justify-between"
          style={{ backgroundColor: `${color}20` }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: color }}
            >
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">{marker.tell}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs font-mono bg-white/20 text-white px-1.5 py-0.5 rounded">
                  @ {formatTime(marker.timestamp)}
                </span>
                <ConfidenceBadge confidence={marker.confidence} size="sm" />
              </div>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {marker.description && (
          <div className="px-4 py-3 border-t border-white/10">
            <p className="text-sm text-white/90 leading-relaxed">{marker.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// TIMELINE WITH MARKERS
// =============================================================================

function VideoTimeline({ currentTime, duration, markers, onSeek, onMarkerClick }) {
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    onSeek(percent * duration);
  };

  return (
    <div className="relative h-8 group">
      {/* Track background */}
      <div
        className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1.5 bg-zinc-700/50 rounded-full cursor-pointer"
        onClick={handleClick}
      >
        {/* Progress fill */}
        <div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Markers */}
      {markers.map((marker, idx) => {
        const position = duration > 0 ? (marker.timestamp / duration) * 100 : 0;
        const level = getConfidenceLevel(marker.confidence);
        const color = getConfidenceColor(level);
        const isNear = Math.abs(currentTime - marker.timestamp) < 0.5;

        return (
          <button
            key={idx}
            onClick={() => onMarkerClick(marker)}
            className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-zinc-900 transition-all duration-200 hover:scale-125 ${
              isNear ? 'scale-125 ring-2 ring-white/50' : ''
            }`}
            style={{
              left: `${position}%`,
              transform: `translateX(-50%) translateY(-50%)`,
              backgroundColor: color,
            }}
            title={`${marker.tell} @ ${formatTime(marker.timestamp)}`}
          />
        );
      })}

      {/* Playhead */}
      <div
        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg transition-all"
        style={{ left: `${progressPercent}%`, transform: 'translateX(-50%) translateY(-50%)' }}
      />
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function BiomechanicsVideoModal({
  analysis,
  allIndicatorsForPitchType = [],
  onClose,
  onNavigatePrev,
  onNavigateNext,
  currentIndex,
  totalCount,
  className = '',
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [selectedMarkerIdx, setSelectedMarkerIdx] = useState(null);
  const visitedMarkersRef = useRef(new Set());

  // Video URL
  const videoUrl = useMemo(() => {
    if (!analysis.gcsUri) return null;
    return getVideoUrl(analysis.gcsUri);
  }, [analysis.gcsUri]);

  // Extract filename
  const filename = useMemo(() => extractFilename(analysis.gcsUri), [analysis.gcsUri]);

  // Build markers from analysis indicators
  const markers = useMemo(() => {
    const allMarkers = [];
    const indicators = analysis.indicators || analysis.tippingIndicators || [];

    for (const indicator of indicators) {
      let timestamp = null;
      if (indicator.timestamp) {
        timestamp = parseTimestampToSeconds(indicator.timestamp);
      }
      if (timestamp === null && indicator.description) {
        const parsed = parseTimestampsFromText(indicator.description);
        if (parsed.length > 0) timestamp = parsed[0];
      }

      if (timestamp !== null && timestamp >= 0) {
        allMarkers.push({
          timestamp,
          tell: indicator.tell || indicator.category || 'Unknown',
          category: indicator.category,
          confidence: indicator.confidence || 0.5,
          description: indicator.description || indicator.whatToLookFor || '',
          pitchTypesAffected: indicator.pitchTypesAffected || indicator.pitchTypesRevealed || [],
        });
      }
    }

    return allMarkers.sort((a, b) => a.timestamp - b.timestamp);
  }, [analysis.indicators, analysis.tippingIndicators]);

  // Combine with all indicators for pitch type (for sidebar display)
  const allBiomarkers = useMemo(() => {
    // Start with markers from this video
    const combined = [...markers];

    // Add any indicators that might be general for this pitch type
    allIndicatorsForPitchType.forEach(ind => {
      const exists = combined.some(m => m.tell === (ind.tell || ind.category));
      if (!exists) {
        combined.push({
          timestamp: null,
          tell: ind.tell || ind.category || 'Unknown',
          category: ind.category,
          confidence: ind.confidence || ind.avg_confidence || 0.5,
          description: ind.description || ind.example_description || '',
          pitchTypesAffected: ind.pitchTypesAffected || [],
          isGeneral: true, // Not from this specific video
        });
      }
    });

    return combined;
  }, [markers, allIndicatorsForPitchType]);

  // Auto-pause at marker timestamps
  useEffect(() => {
    if (!isPlaying || markers.length === 0) return;

    const threshold = 0.3;
    const markerToTrigger = markers.find(m => {
      const timeDiff = Math.abs(m.timestamp - currentTime);
      const markerKey = `${m.timestamp}-${m.tell}`;
      return timeDiff < threshold && !visitedMarkersRef.current.has(markerKey);
    });

    if (markerToTrigger) {
      const markerKey = `${markerToTrigger.timestamp}-${markerToTrigger.tell}`;
      visitedMarkersRef.current.add(markerKey);
      videoRef.current?.pause();
      setIsPlaying(false);
      setActiveMarker(markerToTrigger);
    }
  }, [currentTime, markers, isPlaying]);

  // Reset state when video changes
  useEffect(() => {
    visitedMarkersRef.current.clear();
    setActiveMarker(null);
    setSelectedMarkerIdx(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(true);
    setError(null);
    if (videoRef.current) videoRef.current.currentTime = 0;
  }, [analysis.videoUid, analysis.gcsUri]);

  // Video event handlers
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleError = useCallback(() => {
    setError('Failed to load video');
    setIsLoading(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const handleSeek = useCallback((time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const handleMarkerClick = useCallback((marker) => {
    if (marker.timestamp !== null) {
      handleSeek(marker.timestamp);
      setActiveMarker(marker);
      const idx = allBiomarkers.findIndex(m => m.tell === marker.tell);
      setSelectedMarkerIdx(idx >= 0 ? idx : null);
    }
  }, [handleSeek, allBiomarkers]);

  const skipTime = useCallback((delta) => {
    if (videoRef.current) {
      const newTime = Math.max(0, Math.min(duration, currentTime + delta));
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [currentTime, duration]);

  // Frame-by-frame stepping (~1/30s per frame)
  const FRAME_DURATION = 1 / 30;
  const stepFrame = useCallback((direction) => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
      const newTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + (direction * FRAME_DURATION)));
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  // Playback speed control
  const SPEED_OPTIONS = [0.1, 0.25, 0.5, 1, 1.5, 2];
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const cycleSpeed = useCallback(() => {
    setPlaybackSpeed(prev => {
      const idx = SPEED_OPTIONS.indexOf(prev);
      const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
      if (videoRef.current) videoRef.current.playbackRate = next;
      return next;
    });
  }, []);

  // Sync playback speed when video loads
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackSpeed;
  }, [videoUrl, playbackSpeed]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === ' ') { e.preventDefault(); togglePlay(); }
      if (e.key === 'ArrowLeft' && e.shiftKey) stepFrame(-1);
      else if (e.key === 'ArrowLeft') skipTime(-5);
      if (e.key === 'ArrowRight' && e.shiftKey) stepFrame(1);
      else if (e.key === 'ArrowRight') skipTime(5);
      if (e.key === 'm') toggleMute();
      if (e.key === 's') cycleSpeed();
      if (e.key === ',') stepFrame(-1);
      if (e.key === '.') stepFrame(1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, togglePlay, skipTime, stepFrame, toggleMute, cycleSpeed]);

  const level = getConfidenceLevel(analysis.confidence);
  const color = getConfidenceColor(level);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex"
      onClick={onClose}
    >
      <div
        className={`flex flex-col lg:flex-row w-full h-full ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Video Section */}
        <div className="flex-1 flex flex-col bg-zinc-950 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-3 min-w-0">
              <PitchTypeBadge pitchType={analysis.pitchType || 'UNK'} size="lg" />
              <ConfidenceBadge confidence={analysis.confidence || 0} size="md" />
              <SituationBadge situation={analysis.situation} />
            </div>

            <div className="flex items-center gap-2">
              {/* Navigation */}
              {totalCount > 1 && (
                <div className="flex items-center gap-1 mr-2">
                  <button
                    onClick={onNavigatePrev}
                    disabled={currentIndex === 0}
                    className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <span className="text-sm text-zinc-400 px-2 font-mono">
                    {currentIndex + 1} / {totalCount}
                  </span>
                  <button
                    onClick={onNavigateNext}
                    disabled={currentIndex === totalCount - 1}
                    className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* File Name Bar */}
          <div className="flex items-center gap-2 px-4 py-2 bg-zinc-900/50 border-b border-zinc-800">
            <FileVideo className="w-4 h-4 text-zinc-500" />
            <span className="text-xs font-mono text-zinc-400 truncate" title={filename}>
              {filename}
            </span>
            {analysis.gameDate && (
              <>
                <span className="text-zinc-600">|</span>
                <span className="text-xs text-zinc-500">{analysis.gameDate}</span>
              </>
            )}
            {analysis.pitcher && (
              <>
                <span className="text-zinc-600">|</span>
                <span className="text-xs text-zinc-400">{analysis.pitcher}</span>
              </>
            )}
          </div>

          {/* Video Container */}
          <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
                <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                  <p className="text-red-400">{error}</p>
                </div>
              </div>
            )}

            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                className="max-w-full max-h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onError={handleError}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                muted={isMuted}
                playsInline
              />
            )}

            {/* Active Tell Overlay */}
            {activeMarker && (
              <ActiveTellOverlay
                marker={activeMarker}
                onDismiss={() => setActiveMarker(null)}
              />
            )}

            {/* Play/Pause overlay on click */}
            <div
              className="absolute inset-0 cursor-pointer"
              onClick={togglePlay}
            />
          </div>

          {/* Controls Bar */}
          <div className="px-4 py-3 bg-zinc-900 border-t border-zinc-800">
            {/* Timeline */}
            <VideoTimeline
              currentTime={currentTime}
              duration={duration}
              markers={markers}
              onSeek={handleSeek}
              onMarkerClick={handleMarkerClick}
            />

            {/* Controls */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => skipTime(-5)}
                  className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                  title="Skip back 5s"
                >
                  <SkipBack className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={() => stepFrame(-1)}
                  className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                  title="Step back 1 frame (Shift+Left or ,)"
                >
                  <ChevronLeft className="w-4 h-4 text-zinc-300" />
                </button>
                <button
                  onClick={togglePlay}
                  className="p-3 rounded-full bg-orange-500 hover:bg-orange-400 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-white" />
                  ) : (
                    <Play className="w-6 h-6 text-white ml-0.5" />
                  )}
                </button>
                <button
                  onClick={() => stepFrame(1)}
                  className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                  title="Step forward 1 frame (Shift+Right or .)"
                >
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </button>
                <button
                  onClick={() => skipTime(5)}
                  className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                  title="Skip forward 5s"
                >
                  <SkipForward className="w-5 h-5 text-white" />
                </button>
                <div className="w-px h-5 bg-zinc-700 mx-1" />
                <button
                  onClick={cycleSpeed}
                  className="px-2 py-1.5 rounded-lg hover:bg-zinc-800 transition-colors text-xs font-mono text-amber-400 min-w-[44px]"
                  title="Playback speed (S key)"
                >
                  {playbackSpeed}x
                </button>
                <button
                  onClick={toggleMute}
                  className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-zinc-400" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-mono text-zinc-400">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                {markers.length > 0 && (
                  <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs font-medium">
                    {markers.length} tell{markers.length !== 1 ? 's' : ''} detected
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Biomechanical Markers Panel */}
        <div className="w-full lg:w-96 bg-zinc-900 border-l border-zinc-800 flex flex-col overflow-hidden">
          {/* Sidebar Header */}
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-400" />
              <h3 className="font-bold text-white">Biomechanical Markers</h3>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              {analysis.pitchType ? `Indicators for ${PITCH_TYPE_LABELS[analysis.pitchType] || analysis.pitchType}` : 'All detected indicators'}
            </p>
          </div>

          {/* Context Summary */}
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-800/30">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Pitch Type</span>
                <div className="flex items-center gap-2 mt-1">
                  <Target className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-bold text-white">
                    {PITCH_TYPE_LABELS[analysis.pitchType] || analysis.pitchType || 'Unknown'}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Situation</span>
                <div className="flex items-center gap-2 mt-1">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-white">
                    {formatSituation(analysis.situation)}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Tipping Risk</span>
                <div className="flex items-center gap-2 mt-1">
                  <TrendingUp className="w-4 h-4" style={{ color }} />
                  <span className="text-sm font-bold" style={{ color }}>
                    {level}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wide">Detection</span>
                <div className="flex items-center gap-2 mt-1">
                  <Eye className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium text-white">
                    {analysis.tippingDetected ? 'Tipping Detected' : 'No Tipping'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Markers List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {allBiomarkers.length === 0 ? (
              <div className="text-center py-8">
                <Eye className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No biomechanical markers detected</p>
              </div>
            ) : (
              allBiomarkers.map((marker, idx) => (
                <BiomarkerCard
                  key={`${marker.tell}-${idx}`}
                  marker={marker}
                  isActive={selectedMarkerIdx === idx}
                  currentTime={currentTime}
                  onClick={() => handleMarkerClick(marker)}
                />
              ))
            )}
          </div>

          {/* Analysis Notes */}
          {analysis.analysisNotes && (
            <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-800/30">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-2">
                Analysis Notes
              </h4>
              <p className="text-xs text-zinc-400 leading-relaxed">
                {analysis.analysisNotes}
              </p>
            </div>
          )}

          {/* Hitter Advantage */}
          {analysis.hitterAdvantage && (
            <div className="px-4 py-3 border-t border-zinc-800 bg-orange-500/10">
              <h4 className="text-xs font-semibold text-orange-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Zap className="w-3 h-3" />
                Hitter Advantage
              </h4>
              <p className="text-xs text-orange-200 leading-relaxed">
                {analysis.hitterAdvantage}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { BiomechanicsVideoModal };
